import {
    blueBlackShade,
    blueDarkFades,
    blueishWhiteColor,
    blueWhiteShade,
    challanAmberBg,
    challanAmberBorder,
    challanAmberDark,
    challanAmberFlag,
    challanAmberMid,
    challanBadgeOverlay,
    challanBlueBg,
    challanBlueInfoBorder,
    challanBlueMid,
    challanBorderCard,
    challanBorderForm,
    challanBorderInput,
    challanBorderSoft,
    challanDisabledBg,
    challanGreenBg,
    challanGreenBorder,
    challanGreenDark,
    challanHeaderTableBg,
    challanIndigoBg,
    challanIndigoBorder,
    challanIndigoDark,
    challanIndigoMid,
    challanModalBlue,
    challanModalBlueDark,
    challanRedBg,
    challanRedBgAlt,
    challanRedDark,
    challanRedMid,
    challanRedSoft,
    challanRowDiffBg,
    challanSigHeaderBg,
    challanSubmitGreenDark,
    challanSubmitGreenMid,
    challanSurfaceCard,
    challanSurfaceDeep,
    challanTextDeep,
    challanTextMuted2,
    challanTextMuted3,
    challanTextSlate,
    chocolateLight,
    fadeViolet,
    fadeWhiteShade,
    greenLimeLight,
    greenLimeLightv1,
    redStyleShade,
    slightBluishTintColor,
    slightOrange,
    softRedShadev2,
    tealDark,
    tealFadeBg,
    tealLightBg,
    tealMid,
    tealMidLight,
    tealMintBg,
    tealPaleBg,
    tealPaleBorder,
    violetLight,
    white,
    whiteLightShade,
    whiteShadeColor,
    whiteSlightBlue
} from "../../../config/ColorObj";

export const colors = {
    redMid: "#B83232",
    redBorder: redStyleShade,
    redDark: "#5C1515",
    redBg: "#FFF5F5",
    greenMid: greenLimeLight,
    amberMid: challanAmberMid,

    // ── Teal (Sign-off)
    teal: tealDark,
    tealLight: "#E6F7F4",
    tealMid: "#1A9E80",
    tealDark: "#0A3D33",
    tealBg: "#F0FBF9",

    // ── Neutrals
    gray: "#5F5E5A",
    grayLight: "#F4F2EC",
    border: whiteLightShade,
    borderLight: challanBlueMid,
    surface: whiteShadeColor,
    sectionCardBg: "#34a9f4",
    surfaceHover: "#F1F5F9",
    bg: "#F0F2F5",
    text: "#0F1117",
    textMuted: slightBluishTintColor,
    textSub: blueWhiteShade,
    white: white,
    shadow: "rgba(15,17,23,0.06)",
    shadowMd: "rgba(15,17,23,0.10)",
};

// ── Section theme map — drives header gradient + icon color per section
export const sectionThemes = {
    doc: {
        gradient: `linear-gradient(135deg, ${blueDarkFades} 0%, ${challanBlueMid} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
    stock: {
        gradient: `linear-gradient(135deg, ${challanGreenDark} 0%, ${greenLimeLightv1} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
    payment: {
        gradient: `linear-gradient(135deg, ${chocolateLight} 0%, ${challanAmberMid} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
    dues: {
        gradient: `linear-gradient(135deg, ${violetLight} 0%, ${challanIndigoMid} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
    damage: {
        gradient: `linear-gradient(135deg, ${colors.redDark} 0%, ${colors.redMid} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
    signoff: {
        gradient: `linear-gradient(135deg, ${colors.tealDark} 0%, ${colors.tealMid} 100%)`,
        iconBg: "rgba(255,255,255,0.18)",
        badgeBg: "rgba(255,255,255,0.22)",
        badgeColor: colors.white,
    },
};

export const S = {
    // ─────────────────────── Layout ───────────────────────
    page: {
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
        minHeight: "100vh",
        padding: "20px",
        color: colors.text,
        fontSize: 12,
        boxSizing: "border-box",
    },

    container: {
        margin: "0 auto",
    },

    // ─────────────────────── Section card ───────────────────────
    section: {
        background: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 16,
        boxShadow: `0 2px 8px ${colors.shadow}`,
        transition: "box-shadow 0.2s ease",
    },

    // Base header — overridden per section via theme
    sectionHeader: (theme) => ({
        padding: "13px 20px",
        background: colors.sectionCardBg,
        borderBottom: theme ? "none" : `1px solid ${colors.borderLight}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        position: "relative",
    }),

    sectionTitle: (theme) => ({
        fontSize: 14,
        fontWeight: 700,
        color: theme ? colors.white : colors.text,
        display: "flex",
        alignItems: "center",
        gap: 9,
        letterSpacing: "0.01em",
    }),

    sectionIconWrap: (theme) => ({
        width: 28,
        height: 28,
        borderRadius: 8,
        background: theme ? theme.iconBg : colors.sectionCardBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backdropFilter: "blur(4px)",
    }),

    sectionBody: {
        padding: "18px 20px",
        width: "100%",
        boxSizing: "border-box",
    },

    sectionLabel: {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: colors.textMuted,
        marginBottom: 10,
    },

    sectionLabelFirst: {
        marginTop: 0,
    },

    // ─────────────────────── Badges ───────────────────────
    badge: (variant, onDark) => {
        if (onDark) {
            return {
                display: "inline-block",
                fontSize: 12,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 20,
                background: challanBadgeOverlay,
                color: colors.white,
                whiteSpace: "nowrap",
                border: "1px solid rgba(255,255,255,0.30)",
                backdropFilter: "blur(4px)",
            };
        }
        const map = {
            green: { bg: challanGreenBg, color: challanGreenDark },
            amber: { bg: challanAmberBg, color: challanAmberDark },
            red: { bg: softRedShadev2, color: challanRedDark },
            blue: { bg: challanBlueBg, color: blueDarkFades },
            gray: { bg: colors.grayLight, color: colors.gray },
        };
        const { bg, color } = map[variant] || map.gray;
        return {
            display: "inline-block",
            fontSize: 12,
            fontWeight: 700,
            padding: "3px 10px",
            borderRadius: 20,
            background: bg,
            color,
            whiteSpace: "nowrap",
        };
    },

    badgeGroup: {
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
    },

    // ─────────────────────── Flag / Alert ───────────────────────
    flag: (variant) => {
        const map = {
            warn: { bg: challanAmberBg, border: challanAmberFlag },
            err: { bg: softRedShadev2, border: colors.redBorder },
            ok: { bg: challanGreenBg, border: challanGreenBorder },
            info: { bg: challanBlueBg, border: challanBlueInfoBorder },
        };
        const { bg, border } = map[variant] || map.info;
        return {
            borderRadius: 10,
            padding: "11px 15px",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            marginBottom: 14,
            border: `1px solid ${border}`,
            background: bg,
        };
    },

    flagDot: (color) => ({
        width: 7,
        height: 7,
        borderRadius: "50%",
        flexShrink: 0,
        marginTop: 4,
        background: color,
    }),

    flagText: {
        fontSize: 12,
        lineHeight: 1.6,
        color: colors.textSub,
    },

    // ─────────────────────── Info grids ───────────────────────
    grid3: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 10,
        marginBottom: 12,
    },

    grid2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        marginBottom: 12,
    },

    grid4: {
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 10,
    },

    grid3Responsive: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        gap: 10,
        marginBottom: 12,
    },

    grid2Responsive: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(550px, 1fr))",
        gap: 10,
        marginBottom: 12,
    },

    // ─────────────────────── Info card ───────────────────────
    iCard: {
        background: colors.surface,
        borderRadius: 10,
        padding: "12px 14px",
        border: `1px solid ${colors.borderLight}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        transition: "border-color 0.18s, box-shadow 0.18s",
    },

    iCardLabel: {
        fontSize: 12,
        color: colors.textMuted,
        marginBottom: 3,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 600,
    },

    iCardValue: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.text,
        lineHeight: 1.3,
    },

    iCardSub: {
        fontSize: 12,
        color: colors.textMuted,
        marginTop: 3,
    },

    // ─────────────────────── Header layout ───────────────────────
    headerTopGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        gap: 12,
        marginBottom: 16,
    },

    headerBottomGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
        alignItems: "stretch",
    },

    headerInfoColumn: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%",
    },

    kpiContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        background: fadeWhiteShade,
        border: `1px solid ${challanBlueBg}`,
        borderRadius: 12,
        padding: 12,
    },

    longText: {
        fontSize: 12,
        lineHeight: 1.65,
        fontWeight: 500,
        wordBreak: "break-word",
        color: colors.textSub,
    },

    // ─────────────────────── KPI strip ───────────────────────
    kpiStrip: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
        gap: 10,
    },

    kpi: {
        background: colors.white,
        borderRadius: 10,
        padding: "12px 10px",
        border: `1px solid ${colors.borderLight}`,
        textAlign: "center",
        boxShadow: `0 1px 4px ${colors.shadow}`,
    },

    kpiLabel: {
        fontSize: 12,
        color: colors.textMuted,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },

    kpiValue: {
        fontSize: 18,
        fontWeight: 700,
        margin: "4px 0 2px",
    },

    kpiSub: {
        fontSize: 12,
        color: colors.textMuted,
    },

    // ─────────────────────── Table ───────────────────────
    tableWrapper: {
        overflowX: "auto",
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        marginBottom: 10,
    },

    thead: {
        background: challanHeaderTableBg,
    },

    th: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.textSub,
        padding: "10px 11px",
        textAlign: "left",
        borderBottom: `1px solid ${colors.border}`,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        whiteSpace: "nowrap",
    },

    thRight: { textAlign: "right" },
    thCenter: { textAlign: "center" },

    td: {
        fontSize: 12,
        color: colors.text,
        padding: "10px 11px",
        borderBottom: `1px solid #F3F4F6`,
        verticalAlign: "middle",
    },

    tdRight: { textAlign: "right" },
    tdCenter: { textAlign: "center" },

    rowDiff: { background: challanRowDiffBg },
    rowErr: { background: challanRedBgAlt },

    skuName: { fontWeight: 600, fontSize: 12 },
    skuCode: { fontSize: 12, color: colors.textMuted, marginTop: 2 },

    tableFootnote: {
        background: colors.surface,
        borderTop: `1px solid ${colors.borderLight}`,
        padding: "9px 16px",
        fontSize: 12,
        color: colors.textMuted,
        borderRadius: "0 0 10px 10px",
    },

    // ─────────────────────── Cell inputs ───────────────────────
    cellInput: {
        width: 56,
        padding: "5px 7px",
        border: `1px solid ${colors.border}`,
        borderRadius: 7,
        fontSize: 12,
        textAlign: "center",
        background: colors.white,
        color: colors.text,
        outline: "none",
        boxSizing: "border-box",
        transition: "border-color 0.15s",
    },

    cellSelect: {
        width: "100%",
        padding: "5px 7px",
        border: `1px solid ${colors.border}`,
        borderRadius: 7,
        fontSize: 12,
        background: colors.white,
        color: colors.text,
        outline: "none",
        cursor: "pointer",
    },

    // ─────────────────────── Diff text ───────────────────────
    diffNeg: { fontWeight: 700, color: colors.redMid },
    diffPos: { fontWeight: 700, color: challanGreenDark },
    diffZero: { color: colors.textMuted },
    diffPending: { color: "#C0C0C0" },

    // ─────────────────────── Progress bar ───────────────────────
    progBg: {
        background: "#E9EDF2",
        borderRadius: 4,
        height: 3,
        width: "100%",
    },

    progFill: (pct, color) => ({
        height: 3,
        borderRadius: 4,
        width: `${pct}%`,
        background: color,
        transition: "width 0.35s ease",
    }),

    // ─────────────────────── Payment layout ───────────────────────
    payLayout: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 20,
        alignItems: "stretch",
        width: "100%",
    },

    payModeList: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
    },

    payModeRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "13px 16px",
        background: colors.surface,
        borderRadius: 10,
        border: `1px solid ${colors.borderLight}`,
        gap: 12,
        transition: "border-color 0.15s, box-shadow 0.15s",
    },

    pmLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: colors.text,
    },

    pmSub: {
        fontSize: 12,
        color: colors.textMuted,
        marginTop: 2,
    },

    pmRight: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        minWidth: 130,
        justifyContent: "flex-end",
    },

    pmSymbol: {
        fontSize: 12,
        fontWeight: 600,
        color: colors.textMuted,
    },

    pmInput: {
        width: 110,
        padding: "8px 10px",
        border: `1px solid ${colors.borderLight}`,
        borderRadius: 8,
        fontSize: 12,
        textAlign: "right",
        background: colors.white,
        color: colors.text,
        outline: "none",
        fontFamily: "inherit",
        transition: "border-color 0.15s",
    },

    chequeDetailBox: {
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: "13px 16px",
        marginTop: 12,
    },

    cdbTitle: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.textSub,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 10,
    },

    cdbGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 12,
    },

    cdbLabel: {
        fontSize: 12,
        color: colors.textMuted,
        marginBottom: 4,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },

    cdbInput: {
        padding: "7px 9px",
        border: `1px solid ${colors.border}`,
        borderRadius: 7,
        fontSize: 12,
        background: colors.white,
        color: colors.text,
        width: "100%",
        boxSizing: "border-box",
        outline: "none",
        fontFamily: "inherit",
        transition: "border-color 0.15s",
    },

    // ─────────────────────── Collection summary ───────────────────────
    collectionSummary: {
        background: colors.surface,
        borderRadius: 12,
        padding: "16px 18px",
        border: `1px solid ${colors.borderLight}`,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxSizing: "border-box",
    },

    csTitle: {
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: colors.textMuted,
        marginBottom: 12,
    },

    csRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontSize: 12,
        padding: "7px 0",
        borderBottom: `1px dashed ${colors.borderLight}`,
        color: colors.textSub,
    },

    csRowLast: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        fontWeight: 700,
        fontSize: 12,
        padding: "11px 0 4px",
        marginTop: 6,
        color: colors.text,
    },

    csLabel: {
        color: colors.textMuted,
    },

    progWrap: {
        marginTop: 14,
    },

    progLabel: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: 12,
        color: colors.textMuted,
        marginBottom: 5,
        fontWeight: 600,
    },

    // ─────────────────────── Outstanding dues ───────────────────────
    duesContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 14,
        width: "100%",
    },

    duesList: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        width: "100%",
    },

    duesRow: {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        padding: "13px 18px",
        background: colors.surface,
        borderRadius: 10,
        border: `1px solid ${colors.borderLight}`,
        gap: 16,
        width: "100%",
        transition: "border-color 0.15s, box-shadow 0.15s",
    },

    drBill: {
        fontSize: 12,
        fontWeight: 600,
        color: colors.text,
    },

    drMeta: {
        fontSize: 12,
        color: colors.textMuted,
        marginTop: 2,
    },

    drRight: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 2,
    },

    drAmt: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.redMid,
    },

    drAge: {
        fontSize: 12,
        color: colors.textMuted,
    },

    // ─────────────────────── Damage section ───────────────────────
    dmgContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 16,
        width: "100%",
    },

    dmgGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1.2fr",
        gap: 14,
        alignItems: "stretch",
        width: "100%",
    },

    // ─────────────────────── Sign-off ───────────────────────
    signContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 16,
        width: "100%",
    },

    controlBlock: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },

    signLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: colors.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },

    signLine: {
        borderBottom: "1.5px dashed #D1D5DB",
        margin: "20px 0 8px",
    },

    signName: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.text,
    },

    signSub: {
        fontSize: 12,
        color: colors.textMuted,
        marginTop: 2,
    },

    // ─────────────────────── Shared inputs ───────────────────────
    fullSelect: {
        marginTop: 4,
        padding: "9px 11px",
        borderRadius: 8,
        border: `1px solid ${colors.border}`,
        fontSize: 12,
        background: colors.white,
        color: colors.text,
        outline: "none",
        width: "100%",
        cursor: "pointer",
        boxSizing: "border-box",
        fontFamily: "inherit",
        transition: "border-color 0.15s",
    },

    remarkBlock: {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        boxSizing: "border-box",
    },

    fieldLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: colors.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },

    textarea: {
        width: "100%",
        padding: "10px 13px",
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        fontSize: 12,
        resize: "vertical",
        minHeight: 60,
        background: colors.white,
        color: colors.text,
        outline: "none",
        lineHeight: 1.32,
        boxSizing: "border-box",
        fontFamily: "inherit",
        transition: "border-color 0.15s",
    },

    // ─────────────────────── Action bar ───────────────────────
    actionBar: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        paddingTop: 6,
        width: "100%",
    },

    btn: {
        padding: "10px 22px",
        borderRadius: 9,
        border: `1px solid ${colors.border}`,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        background: colors.white,
        color: colors.textSub,
        transition: "background 0.15s, border-color 0.15s",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
    },

    btnHold: {
        padding: "10px 22px",
        borderRadius: 9,
        border: `1px solid ${colors.redBorder}`,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        background: softRedShadev2,
        color: challanRedDark,
        transition: "background 0.15s",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
    },

    flagWrapper: {
        margin: "14px 20px 0",
    },

    // ── Table section ────────────────────────────────────────────────────────
    tableSection: {
        padding: "2px 20px 18px",
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
    },

    thSub: {
        color: colors.textMuted,
        fontWeight: 400,
        textTransform: "none",
        fontSize: 10,
    },

    skuSeq: {
        color: colors.textMuted,
        fontSize: 13,
    },

    // ── Rate cell ────────────────────────────────────────────────────────────
    rateValue: {
        fontSize: 13,
        fontWeight: 600,
        color: challanBlueMid,
    },
    // ── Reason dropdown wrapper ──────────────────────────────────────────────
    reasonWrapper: {
        minWidth: 180,
        width: "100%",
    },

    reasonEmpty: {
        color: colors.border,
        fontSize: 12,
    },

    // ── Sell-through percentage ──────────────────────────────────────────────
    sellPctHigh: {
        fontSize: 14,
        fontWeight: 700,
        color: greenLimeLight,
    },

    sellPctMid: {
        fontSize: 14,
        fontWeight: 700,
        color: challanAmberMid,
    },

    sellPctLow: {
        fontSize: 14,
        fontWeight: 700,
        color: colors.redBorder,
    },

    btnSubmit: (isEnabled) => ({
        padding: "10px 22px",
        borderRadius: "9px",
        fontSize: 12,
        fontWeight: 550,
        cursor: isEnabled ? "pointer" : "not-allowed",
        background: `linear-gradient(135deg, ${challanSubmitGreenDark}, ${challanSubmitGreenMid})`,
        color: white,
        transition: "all 0.15s ease",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        opacity: isEnabled ? 1 : 0.6,

        "&:hover": isEnabled
            ? {
                background: "linear-gradient(135deg, #0a4a38, #0F6E56)",
            }
            : {},
    }),
};

export const headerTopStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))",
    gap: 12,
    marginBottom: 16,
};

export const settlementValueStyle = {
    color: challanBlueMid,
};

export const damageValueStyle = {
    color: colors.redMid,
};

export const responsibilitySelectStyle = {
    ...S.fullSelect,
    marginTop: 6,
};

export const leftColumnStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
};

export const rightColumnStyle = {
    display: "flex",
    flexDirection: "column",
};

export const chequeInputDisabledStyle = {
    background: whiteSlightBlue,
    color: challanTextMuted3,
    cursor: "not-allowed",
    borderColor: whiteLightShade,
};

export const addChequeBtnStyle = {
    width: "88%",
    padding: "4px",
    borderRadius: 8,
    border: `2px dotted ${challanBlueMid}`,
    background: challanBlueBg,
    color: blueDarkFades,
    textAlign: "center",
    fontSize: 10,
    fontWeight: 500,
    cursor: "pointer",
};

export const amountInputWrapStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
};

export const payModeRightInnerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 10,
};

export const signRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
};

export const signerAvatarStyle = {
    width: 28,
    height: 28,
    borderRadius: "50%",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
};

export const signerNameStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: colors.text,
    lineHeight: 1.3,
};

export const signerSubStyle = {
    fontSize: 11,
    color: colors.textMuted,
};

export const checkboxStyle = {
    width: 18,
    height: 18,
    cursor: "pointer",
    flexShrink: 0,
    accentColor: blueDarkFades,
};

export const signaturesCardStyle = {
    border: `1px solid ${whiteLightShade}`,
    borderRadius: 10,
    background: white,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
};

export const signaturesHeaderStyle = {
    padding: "6px 12px",
    background: whiteShadeColor,
    borderBottom: `1px solid challanBorderSoft`,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: colors.textMuted,
};

export const signaturesGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    flex: 1,
};

export const signaturesFooterStyle = {
    display: "flex",
    gap: 6,
    padding: "6px 12px",
    borderTop: `1px solid ${challanBorderSoft}`,
    background: whiteShadeColor,
};

export const topGridStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) minmax(0,2fr)",
    gap: 10,
    alignItems: "stretch",
};

export const pageLayoutStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 10,
};

export const signoffColors = {
    // Status pill – "done"
    pillDoneBg: challanGreenBg,
    pillDoneText: challanGreenDark,
    pillDoneBorder: challanGreenBorder,
    pillDoneDot: tealMid,

    // Status pill – "pending"
    pillPendingBg: challanAmberBg,
    pillPendingText: challanAmberDark,
    pillPendingBorder: challanAmberBorder,
    pillPendingDot: challanAmberMid,

    // Misc
    signerDivider: challanBorderSoft,
};

// ── SignerCard ────────────────────────────────────────────────────────────────

export const signerCardStyles = {
    /** Applied per-row; borderRight is set dynamically via getSignerRowStyle(). */
    row: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        flex: 1,
    },

    checkbox: {
        width: 16,
        height: 16,
        accentColor: tealMid,
        cursor: "pointer",
        flexShrink: 0,
    },

    /**
     * Base avatar style – background & color are injected at render time
     * via getAvatarStyle(avatarBg, avatarColor).
     */
    avatarBase: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 13,
        flexShrink: 0,
        userSelect: "none",
    },

    name: {
        fontSize: 13,
        fontWeight: 600,
        color: challanTextDeep,
        lineHeight: 1.3,
    },

    sub: {
        fontSize: 11,
        color: challanTextMuted2,
        marginTop: 1,
    },
};

/**
 * Returns the full row style for a SignerCard.
 */
export const getSignerRowStyle = (isLast) => ({
    ...signerCardStyles.row,
    borderRight: isLast ? "none" : `1px solid ${signoffColors.signerDivider}`,
});

/**
 * Returns the full avatar style for a SignerCard.
 */
export const getAvatarStyle = (avatarBg, avatarColor) => ({
    ...signerCardStyles.avatarBase,
    background: avatarBg,
    color: avatarColor,
});

// ── StatusPill ────────────────────────────────────────────────────────────────

export const statusPillStyles = {
    pillBase: {
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 20,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
    },

    dotBase: {
        width: 5,
        height: 5,
        borderRadius: "50%",
        flexShrink: 0,
    },
};

/**
 * Returns the full pill style based on done state.
 */
export const getPillStyle = (done) => ({
    ...statusPillStyles.pillBase,
    background: done ? signoffColors.pillDoneBg : signoffColors.pillPendingBg,
    color: done ? signoffColors.pillDoneText : signoffColors.pillPendingText,
    border: `1px solid ${done ? signoffColors.pillDoneBorder : signoffColors.pillPendingBorder}`,
});

/**
 * Returns the dot style based on done state.
 */
export const getDotStyle = (done) => ({
    ...statusPillStyles.dotBase,
    background: done ? signoffColors.pillDoneDot : signoffColors.pillPendingDot,
});

// ── SignaturesCard ────────────────────────────────────────────────────────────

export const signaturesCardStyles = {
    card: {
        border: `1px solid ${challanBorderCard}`,
        borderRadius: 10,
        overflow: "hidden",
        background: challanSurfaceDeep,
        minWidth: 260,
    },

    header: {
        fontSize: 11,
        fontWeight: 700,
        color: challanTextMuted2,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        padding: "8px 16px",
        borderBottom: `1px solid ${challanBorderCard}`,
        background: challanSigHeaderBg,
    },

    grid: {
        display: "flex",
    },

    footer: {
        display: "flex",
        gap: 8,
        padding: "8px 12px",
        borderTop: `1px solid ${challanBorderCard}`,
        background: challanSigHeaderBg,
        flexWrap: "wrap",
    },
};

// ── Signoff (root) ────────────────────────────────────────────────────────────

export const signoffStyles = {
    remarkBlock: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },

    fieldLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: challanTextSlate,
        letterSpacing: "0.2px",
    },

    textarea: {
        width: "100%",
        minHeight: 80,
        padding: "10px 12px",
        fontSize: 13,
        border: `1px solid ${challanBorderForm}`,
        borderRadius: 8,
        resize: "vertical",
        outline: "none",
        fontFamily: "inherit",
        color: challanTextDeep,
        background: challanSurfaceDeep,
        boxSizing: "border-box",
        lineHeight: 1.5,
        transition: "border-color 0.15s ease",
    },

    actionBar: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "center",
        paddingTop: 4,
        justifyContent: "flex-end",
    },

    btn: {
        fontSize: 13,
        fontWeight: 600,
        padding: "7px 16px",
        borderRadius: 8,
        border: `1px solid ${challanBorderForm}`,
        background: white,
        color: challanTextSlate,
        cursor: "pointer",
    },

    btnHold: {
        fontSize: 13,
        fontWeight: 600,
        padding: "7px 16px",
        borderRadius: 8,
        border: `1px solid ${challanAmberBorder}`,
        background: challanAmberBg,
        color: challanAmberDark,
        cursor: "pointer",
    },
};

/**
 * Returns the submit button style based on enabled state.
 */
export const getBtnSubmitStyle = (enabled) => ({
    fontSize: 13,
    fontWeight: 700,
    padding: "7px 20px",
    borderRadius: 8,
    border: "none",
    background: enabled ? tealMid : challanDisabledBg,
    color: enabled ? white : challanTextMuted2,
    cursor: enabled ? "pointer" : "not-allowed",
    transition: "background 0.2s ease, color 0.2s ease",
});

// ─── ChequeRegister Styles ────────────────────────────────────────────────────

export const chequeRegisterStyles = {
    // ── Wrapper ──
    wrapper: {
        background: white,
        border: `1px solid ${whiteLightShade}`,
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 16,
        boxShadow: "0 2px 8px rgba(15,17,23,0.06)",
        fontFamily: "'DM Sans', system-ui, sans-serif",
    },

    // ── Section Header ──
    sectionHeader: {
        padding: "13px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 9 },
    headerIconBox: {
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "rgba(255,255,255,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 14,
        fontWeight: 700,
        color: white,
        letterSpacing: "0.01em",
    },
    headerBadges: { display: "flex", gap: 6, flexWrap: "wrap" },

    badgeCleared: {
        fontSize: 12,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 20,
        background: challanGreenBg,
        color: challanGreenDark,
        border: `1px solid ${challanGreenBorder}`,
    },
    badgeBounced: {
        fontSize: 12,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 20,
        background: softRedShadev2,
        color: challanRedDark,
        border: `1px solid ${redStyleShade}`,
    },
    badgeOnHold: {
        fontSize: 12,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 20,
        background: challanIndigoBg,
        color: challanIndigoDark,
        border: `1px solid ${challanIndigoBorder}`,
    },

    // ── Cards List ──
    cardsList: {
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },

    // ── Card Top Row ──
    cardTopRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 45,
        padding: "13px 16px 13px 20px",
    },
    cardStatusAndBill: { display: "flex", alignItems: "center", gap: 8 },
    invoiceTag: {
        fontSize: 13,
        fontWeight: 700,
        padding: "3px 9px",
        borderRadius: 20,
        border: `1px solid ${challanBlueInfoBorder}`,
        whiteSpace: "nowrap",
        letterSpacing: "0.04em",
    },
    outletInfo: { flex: "1 1 160px", minWidth: 0 },
    outletName: { fontWeight: 700, fontSize: 14 },
    outletMeta: { fontSize: 13, marginTop: 3 },

    // ── Cheque Fields Box ──
    chequeFieldsBox: {
        display: "flex",
        gap: 50,
        flexWrap: "wrap",
        background: challanSurfaceCard,
        borderRadius: 8,
        padding: "8px 14px",
        border: `1px solid ${challanBorderSoft}`,
    },
    fieldCell: { textAlign: "center", minWidth: 64 },
    fieldLabel: {
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 3,
    },
    fieldValue: { fontSize: 13, fontWeight: 500 },
    fieldValueHighlight: { fontSize: 13, fontWeight: 700 },
    riskPill: {
        fontSize: 11,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 20,
    },

    // ── Card Actions ──
    cardActions: { display: "flex", alignItems: "center", gap: 20 },
    removeBtn: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: "50%",
        cursor: "pointer",
        background: softRedShadev2,
        border: `1px solid ${challanRedSoft}`,
        transition: "all 0.2s ease",
    },
    statusPillDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },

    // ── Result Bars ──
    resultBarCleared: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        padding: "10px 14px",
        background: tealPaleBg,
        borderTop: `1px solid ${tealPaleBorder}`,
        borderRadius: "0 0 12px 12px",
    },
    resultBarClearedText: {
        fontSize: 13,
        color: tealDark,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 4,
    },

    resultBarBounced: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        padding: "10px 14px",
        background: challanRedBg,
        borderTop: `1px solid ${challanRedSoft}`,
        borderRadius: "0 0 12px 12px",
    },
    resultBarBouncedText: { fontSize: 13, color: challanRedMid },
    resultBarBouncedBadge: {
        fontSize: 12,
        fontWeight: 700,
        padding: "3px 10px",
        borderRadius: 20,
        background: challanRedDark,
        color: white,
    },

    resultBarHold: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        background: challanIndigoBg,
        borderTop: `1px solid ${challanIndigoBorder}`,
        borderRadius: "0 0 12px 12px",
    },
    resultBarHoldText: { fontSize: 13, color: challanIndigoDark },
};

// ── Config maps ──

export const CARD_BORDER_MAP = {
    cleared: tealPaleBorder,
    bounced: challanRedSoft,
    hold: challanIndigoBorder,
};

export const RISK_MAP = {
    0: {
        label: "Low",
        bg: challanGreenBg,
        color: challanGreenDark,
        border: challanGreenBorder,
    },
    1: {
        label: "Medium",
        bg: challanAmberBg,
        color: challanAmberDark,
        border: challanAmberBorder,
    },
    2: {
        label: "High",
        bg: softRedShadev2,
        color: challanRedDark,
        border: redStyleShade,
    },
};

export const STATUS_CFG = {
    cleared: { label: "Cleared", dot: tealMid },
    bounced: { label: "Bounced", dot: redStyleShade },
    hold: { label: "On Hold", dot: fadeViolet },
};

export const STATUS_ICON_CFG = {
    cleared: {
        bg: tealLightBg,
        color: tealMid,
        border: tealPaleBorder,
        icon: "✓",
    },
    hold: {
        bg: whiteSlightBlue,
        color: slightBluishTintColor,
        border: challanBorderInput,
        icon: "!",
    },
    bounced: {
        bg: softRedShadev2,
        color: redStyleShade,
        border: challanRedSoft,
        icon: "✕",
    },
};

export const TSuccessModal = {
    ok: tealMidLight,
    okLight: tealFadeBg,
    blue: challanModalBlue,
    amber: slightOrange,
    ink1: blueBlackShade,
    ink2: blueWhiteShade,
    ink3: slightBluishTintColor,
    ink4: challanTextMuted3,
    border: whiteLightShade,
    bg: blueishWhiteColor,
    surface: white,
    font: "'DM Sans', system-ui, sans-serif",
    mono: "'DM Mono', monospace",
};

export const SuccessModalstyles = {
    backdrop: {
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(17, 24, 39, 0.55)",
        backdropFilter: "blur(3px)",
        animation: "suc-backdrop 0.2s ease",
        fontFamily: TSuccessModal.font,
    },
    popup: {
        position: "relative",
        background: TSuccessModal.surface,
        borderRadius: 20,
        width: "100%",
        maxWidth: 420,
        padding: "36px 32px 28px",
        textAlign: "center",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        animation: "suc-popup 0.32s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    closeBtn: {
        position: "absolute",
        top: 14,
        right: 14,
        width: 28,
        height: 28,
        border: `1px solid ${TSuccessModal.border}`,
        borderRadius: 7,
        background: "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: TSuccessModal.ink4,
    },

    // Icon
    iconRing: {
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: tealMintBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 20px",
        animation: "suc-ring 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both",
    },
    iconInner: {
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: TSuccessModal.ok,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 4px 16px ${TSuccessModal.ok}55`,
    },

    // Text
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: TSuccessModal.ink1,
        marginBottom: 6,
    },
    subtitle: {
        fontSize: 13.5,
        color: TSuccessModal.ink3,
        lineHeight: 1.55,
        marginBottom: 22,
    },
    mono: {
        fontFamily: TSuccessModal.mono,
        color: TSuccessModal.ink2,
        fontWeight: 600,
    },

    // Reference card
    refCard: {
        background: TSuccessModal.bg,
        border: `1px solid ${TSuccessModal.border}`,
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 20,
        textAlign: "left",
    },
    refCardHeader: {
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: TSuccessModal.ink4,
        marginBottom: 12,
    },
    refGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "10px 16px",
    },
    refCell: {
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    refLabel: {
        fontSize: 11,
        color: TSuccessModal.ink4,
        fontWeight: 500,
    },
    refValue: {
        fontSize: 13,
        fontWeight: 600,
        color: TSuccessModal.ink2,
    },
    monoVal: {
        fontFamily: TSuccessModal.mono,
        fontSize: 12.5,
    },
    highlightVal: {
        color: TSuccessModal.amber,
    },

    // Done button
    doneBtn: {
        width: "100%",
        padding: "12px",
        borderRadius: 10,
        border: "none",
        background: `linear-gradient(135deg, ${challanModalBlue}, ${challanModalBlueDark})`,
        color: white,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: TSuccessModal.font,
        boxShadow: `0 2px 10px ${challanModalBlue}44`,
    },
};

export const CSSForSuccessModal = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

@keyframes suc-backdrop { from { opacity: 0; } to { opacity: 1; } }
@keyframes suc-popup {
  from { opacity: 0; transform: scale(0.9) translateY(16px); }
  to   { opacity: 1; transform: scale(1)   translateY(0);    }
}
@keyframes suc-ring {
  0%   { transform: scale(0.6); opacity: 0; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1);   opacity: 1; }
}
`;
